import React, { useEffect, useState } from "react";
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  Avatar,
  Typography,
} from "@mui/material";
import {
  Science,
  Settings,
  Logout,
  MoreVert,
  Edit,
  Forum,
  SupportAgent,
  ManageAccounts,
  Folder,
  AddBox,
} from "@mui/icons-material";
import { motion } from "framer-motion"; // Import Framer Motion for smooth transitions
import Logo from "../../Components/Logo/Logo";
import {
  listItemButtonStyles,
  listItemIconStyles,
  logoSection,
  menuWhenClickedStyles,
  motionDivContainer,
  profileLogoutContainer,
  ql_text,
} from "./AppDrawerNavStyles";
import AccountTreeIcon from "@mui/icons-material/AccountTree";

const AppSidebar = ({
  tabName,
  setTabName,
  navigate,
  user,
  handleLogoutApp,
  subscription,
  privilege,
  experimentsCount,
}) => {
  const drawerWidth = 260;
  const [anchorEl, setAnchorEl] = useState(null);
  const [hovered, setHovered] = useState(false);
  const [showText, setShowText] = useState(false);

  useEffect(() => {
    let timer;
    if (hovered) {
      timer = setTimeout(() => setShowText(true), 80);
    } else {
      setShowText(false);
    }

    return () => clearTimeout(timer);
  }, [hovered]);
  const open = Boolean(anchorEl);

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  const handleLogout = () => handleLogoutApp();

  const specialDescriptions = {
    "customize llm app": "Build custom applications with full control.",
    experiments: "Track and optimize application performance.",
    "deployed llm apps": "View and manage deployed applications.",
    dashboard:
      "Launch applications instantly by uploading your documents, or kickstart with our library of pre-built templates.",
  };

  return (
    <motion.div
      initial={{ width: 60 }}
      animate={{ width: hovered ? drawerWidth : 60 }}
      transition={{ duration: 0.2, ease: "linear" }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={motionDivContainer}
    >
      {/* LOGO SECTION */}
      <Box
        sx={{
          ...logoSection,
          justifyContent: hovered ? "flex-start" : "center",
        }}
      >
        <Logo />
        {hovered && <ListItemText primary="Queryloop" sx={ql_text} />}
      </Box>

      {/* MENU ITEMS */}
      <List sx={{ flexGrow: 1 }}>
        {[
          {
            name: "Dashboard",
            icon: <Folder fontSize="small" />,
            route: "/dashboard/main",
            key: "main",
            accessLimit: true,
          },
          {
            name: "Customize LLM App",
            icon: <AccountTreeIcon fontSize="small" />,
            route: "/dashboard/createChatbot",
            key: "createChatbot",
            accessLimit: true,
          },
          {
            name: "Experiments",
            icon: <Science fontSize="small" />,
            route: "/dashboard/myBots",
            key: "myBots",
            accessLimit: true,
          },
          {
            name: "Settings",
            icon: <Settings fontSize="small" />,
            route: "/dashboard/settingsPage",
            key: "settingsPage",
            accessLimit: true,
          },
          {
            name: "Deployed LLM Apps",
            icon: <Forum fontSize="small" />,
            route: "/dashboard/deployedBots",
            key: "deployedBots",
            requiresPro: true,
            accessLimit: false,
          },
          {
            name: "Management",
            icon: <ManageAccounts fontSize="small" />,
            route: "/dashboard/Management",
            key: "Management",
            requiresPro: true,
            accessLimit: true,
          },
          {
            name: "Support",
            icon: <SupportAgent fontSize="small" />,
            route: "/dashboard/contactSupport",
            key: "contactSupport",
            accessLimit: false,
          },
        ].map((item) => {
          const isProRequired = item.requiresPro && subscription === "starter";
          const isAccessLimited = item.accessLimit && privilege === "member";
          return (
            !isAccessLimited && (
              <Tooltip
                key={item.key}
                title={
                  isProRequired
                    ? "You need a Pro subscription to access this feature."
                    : isAccessLimited
                    ? "Only owners or admins can access this feature."
                    : specialDescriptions[item.name.toLowerCase()] ?? item.name
                }
                placement="right"
                arrow
              >
                <span style={{ width: "100%" }}>
                  <ListItemButton
                    onClick={() => {
                      if (!isProRequired && !isAccessLimited) {
                        setTabName(item.key);
                        navigate(item.route);
                      }
                    }}
                    disabled={isProRequired || isAccessLimited}
                    sx={{
                      ...listItemButtonStyles,
                      padding: "6px",
                      color: tabName === item.key ? "#fff" : "#bbb",
                      backgroundColor:
                        tabName === item.key
                          ? "var(--buttonBackground)"
                          : "transparent",
                      "&:hover": { backgroundColor: "#3A3D4A" },
                      opacity: isProRequired ? 0.5 : 1,
                      cursor: isProRequired ? "not-allowed" : "pointer",
                      height: "40px",
                    }}
                  >
                    <ListItemIcon sx={listItemIconStyles}>
                      {item.icon}
                    </ListItemIcon>
                    {showText && (
                      <Typography noWrap sx={{ fontSize: "14px" }}>
                        {item.name}
                        {item.key === "myBots" &&
                          experimentsCount !== undefined &&
                          ` (${experimentsCount})`}
                      </Typography>
                    )}
                  </ListItemButton>
                </span>
              </Tooltip>
            )
          );
        })}
      </List>

      {/* PROFILE & LOGOUT */}
      <Box
        sx={{
          ...profileLogoutContainer,
          justifyContent: hovered ? "space-between" : "center",
        }}
        onClick={handleMenuOpen}
      >
        {/* PROFILE ICON WHEN HOVERED */}
        {hovered ? (
          <>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Avatar
                sx={{ width: 32, height: 32, marginRight: "10px" }}
                src={user?.profilePicture || ""}
              >
                {!user?.profilePicture && user?.first_name?.charAt(0)}
              </Avatar>
              <Typography
                noWrap
                sx={{ color: "#fff", fontSize: "14px" }}
              >{`${user?.user?.first_name} ${user?.user?.last_name}`}</Typography>
            </Box>
            <IconButton sx={{ color: "#fff" }}>
              <MoreVert />
            </IconButton>
          </>
        ) : (
          // ONLY ICON WHEN COLLAPSED
          <IconButton sx={{ color: "#fff" }}>
            <MoreVert />
          </IconButton>
        )}
      </Box>

      {/* MENU WHEN CLICKED */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        sx={menuWhenClickedStyles}
      >
        <MenuItem onClick={handleLogout}>
          <Logout fontSize="small" sx={{ color: "#fff", marginRight: 1 }} />
          Sign Out
        </MenuItem>
        <MenuItem onClick={() => navigate("/dashboard/profile")}>
          <Edit fontSize="small" sx={{ color: "#fff", marginRight: 1 }} />
          Edit Profile
        </MenuItem>
      </Menu>
    </motion.div>
  );
};

export default AppSidebar;
